import React, { useEffect, useState, useCallback } from "react";
import { useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { ENDPOINTS } from "../../../config/api";
import "bootstrap/dist/css/bootstrap.min.css";

const ExternalPostulations = () => {
  const { authFetch } = useContext(AuthContext);

  const [postulations, setPostulations] = useState([]);
  const [filteredPostulations, setFilteredPostulations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [customAlert, setCustomAlert] = useState({ type: "", msg: "" });

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const [showModal, setShowModal] = useState(false);
  const [selectedPostulation, setSelectedPostulation] = useState(null);

  // ---------- alerts ----------
  const showAlert = (type, msg, duration = 3000) => {
    setCustomAlert({ type, msg });
    setTimeout(() => setCustomAlert({ type: "", msg: "" }), duration);
  };

  // ---------- cargar postulaciones ----------
  const loadPostulations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(ENDPOINTS.getMyPostulations);

      if (res && res.success && Array.isArray(res.data)) {
        setPostulations(res.data);
        setFilteredPostulations(res.data);
      } else {
        setPostulations([]);
        setFilteredPostulations([]);
      }
    } catch (err) {
      console.error("Error cargando postulaciones", err);
      showAlert("danger", "No se pudieron cargar las postulaciones.");
      setPostulations([]);
      setFilteredPostulations([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadPostulations();
  }, [loadPostulations]);

  // ---------- filtros ----------
  useEffect(() => {
    let temp = [...postulations];

    if (search.trim() !== "") {
      temp = temp.filter((p) =>
        p.nombreOferta?.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (filterStatus !== "") {
      temp = temp.filter((p) => p.state === filterStatus);
    }

    setFilteredPostulations(temp);
    setCurrentPage(1);
  }, [search, filterStatus, postulations]);

  // ---------- paginación ----------
  const last = currentPage * itemsPerPage;
  const first = last - itemsPerPage;
  const pageData = filteredPostulations.slice(first, last);
  const totalPages = Math.max(1, Math.ceil(filteredPostulations.length / itemsPerPage));

  // ---------- modal ----------
  const openModal = (post) => {
    setSelectedPostulation(post);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPostulation(null);
  };

  return (
    <div className="container py-4">
      {/* Alerts */}
      {customAlert.msg && (
        <div className={`alert alert-${customAlert.type} text-center`} role="alert">
          {customAlert.msg}
        </div>
      )}

      <h2 className="text-center mt-3 fw-bold">MIS POSTULACIONES</h2>
      <p className="text-center">Historial de ofertas a las que te has postulado</p>

      {/* filtros */}
      <div className="card p-3 shadow-sm mb-3">
        <div className="row g-3">
          <div className="col-md-8">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por programa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="col-md-4">
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Estado (todos)</option>
              <option value="En proceso">En proceso</option>
              <option value="Aceptado">Aceptado</option>
              <option value="Denegado">Denegado</option>
            </select>
          </div>
        </div>
      </div>

      {/* tarjetas */}
      {loading ? (
        <p className="text-center">Cargando postulaciones...</p>
      ) : (
        <div className="row g-4 mt-3">
          {pageData.length === 0 ? (
            <p className="text-center">No hay postulaciones con estos filtros.</p>
          ) : (
            pageData.map((post) => (
              <div className="col-md-4" key={post.IdPostulacion}>
                <div className="card shadow-sm h-100 p-3 border-primary d-flex flex-column">
                  <h5 className="mb-2">{post.nombreOferta}</h5>
                  <span className="badge bg-secondary mb-2">ID Oferta: {post.IdOfertas}</span>
                  <p><strong>Estado:</strong> {post.state}</p>
                  <p>
                    <strong>Postulado el:</strong>{" "}
                    {new Date(post.created_at).toLocaleDateString("es-CO")}
                  </p>

                  <div className="mt-auto">
                    <button
                      className="btn btn-outline-purple w-100"
                      onClick={() => openModal(post)}
                    >
                      Ver detalles
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* paginación */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4 align-items-center">
          <button
            className="btn btn-outline-secondary mx-1"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            ◀
          </button>
          <span className="px-3 py-2">
            Página {currentPage} de {totalPages}
          </span>
          <button
            className="btn btn-outline-secondary mx-1"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            ▶
          </button>
        </div>
      )}

      {/* modal */}
      {showModal && selectedPostulation && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h3 className="modal-title">{selectedPostulation.nombreOferta}</h3>
                <button className="btn-close" onClick={closeModal}></button>
              </div>

              <div className="modal-body">
                <p><strong>ID Oferta:</strong> {selectedPostulation.IdOfertas}</p>
                <p>
                  <strong>Fecha de postulación:</strong>{" "}
                  {new Date(selectedPostulation.created_at).toLocaleDateString("es-CO")}
                </p>
                <p><strong>Estado postulacion:</strong> {selectedPostulation.state}</p>

                <hr />

                <p><strong>Descripción:</strong></p>
                <p>{selectedPostulation.descripcionOferta}</p>
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalPostulations;
